import "./Card.scss";

import * as Katex from "katex";
import MarkdownIt from "markdown-it";
import * as MarkdownItTexMath from "markdown-it-texmath";
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import AnimateHeight from "react-animate-height";
import { Transition } from "react-transition-group";

import { ReactComponent as Retry31 } from "../assets/retry/31px-narrow.svg";
import { CardState } from "../db";
import { SpacedRepetitionSchedule } from "../spacedRepetition";
import isTouch from "../util/isTouch";
import QuestionProgressIndicator, {
  QuestionProgressIndicatorHandle,
  ReviewMarkingInteractionState,
} from "./QuestionProgressIndicator";
import { CardType, PromptData } from "../CardData";
import scrollparent from "scrollparent";

const texMath = MarkdownItTexMath.use(Katex);
export const markdownRenderer = new MarkdownIt({ html: true }).use(texMath);

export interface CardProps {
  isRevealed: boolean;
  isOccluded?: boolean;
  showsNeedsRetryNotice?: boolean;
  cardType: CardType;
  promptData: PromptData;
  cardState: CardState | null;
  reviewMarkingInteractionState: ReviewMarkingInteractionState | null;
  schedule: SpacedRepetitionSchedule;
  onToggleExplanation?: (isExplanationExpanded: boolean) => any;
  shouldLabelApplicationPrompts: boolean;
}

export interface CardHandle {
  getLevelItemElements(): HTMLElement[] | null;
}

const CardImpl = React.memo(function Card({
  isRevealed,
  isOccluded,
  showsNeedsRetryNotice,
  cardType,
  promptData: {
    question,
    answer,
    questionAdjustments,
    answerAdjustments,
    explanation,
  },
  cardState,
  reviewMarkingInteractionState,
  schedule,
  onToggleExplanation: clientOnToggleExplanation,
  shouldLabelApplicationPrompts,
  exportedRef,
}: CardProps & { exportedRef: React.Ref<CardHandle> }) {
  const indicatorRef = useRef<QuestionProgressIndicatorHandle | null>(null);
  useImperativeHandle(exportedRef, () => ({
    getLevelItemElements: () =>
      indicatorRef.current ? indicatorRef.current.getLevelItemElements() : null,
  }));

  const revealVerb = isTouch() ? "Tap" : "Click";

  const [isExplanationExpanded, setExplanationExpanded] = useState(false);
  const onToggleExplanation = useCallback(
    (event: React.MouseEvent) => {
      const scrollParent = scrollparent(event.currentTarget as HTMLElement)!;
      setExplanationExpanded(isExplanationExpanded => {
        const newIsExplanationExpanded = !isExplanationExpanded;
        clientOnToggleExplanation?.(newIsExplanationExpanded);
        return newIsExplanationExpanded;
      });
      const initialTimestamp = performance.now();
      const tick = (timestamp: DOMHighResTimeStamp) => {
        scrollParent.scrollTop = scrollParent.scrollHeight;
        if (timestamp - initialTimestamp < 300) {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    },
    [setExplanationExpanded, clientOnToggleExplanation],
  );

  return (
    <div
      className={`Card ${isOccluded ? "Occluded" : ""} ${
        explanation ? "WithExplanation" : ""
      } ${isExplanationExpanded ? "ExplanationExpanded" : ""}`}
    >
      {cardType === "applicationPrompt" && shouldLabelApplicationPrompts && (
        <div className="ApplicationPromptLabel" />
      )}
      <RenderedTextRegion
        className={`Prompt ${
          questionAdjustments ? `Shrink${questionAdjustments}` : ""
        }`}
      >
        {question}
      </RenderedTextRegion>
      <RenderedTextRegion
        className={`Answer ${
          answerAdjustments ? `Shrink${answerAdjustments}` : ""
        }`}
      >
        {answer}
      </RenderedTextRegion>
      {explanation && (
        <div
          className={`Explanation ${
            isExplanationExpanded ? "Explanation-Expanded" : ""
          }`}
        >
          <div className="ExplanationHeading" onClick={onToggleExplanation}>
            <label>Explanation</label>
          </div>
          <AnimateHeight
            duration={150}
            height={isExplanationExpanded ? "auto" : 0}
            easing="ease-out"
          >
            <RenderedTextRegion>{explanation}</RenderedTextRegion>
          </AnimateHeight>
        </div>
      )}
      <div
        className={`LevelContainer ${isRevealed ? "" : "LevelContainerHidden"}`}
      >
        <QuestionProgressIndicator
          cardState={cardState}
          cardType={cardType}
          reviewMarkingInteractionState={reviewMarkingInteractionState}
          schedule={schedule}
          ref={indicatorRef}
        />
      </div>
      <Transition in={!isRevealed} timeout={500}>
        {transitionStatus => (
          <div
            className="AnswerCover"
            style={answerCoverAnimationStyles[transitionStatus] || undefined}
          >
            {transitionStatus !== "exited" && transitionStatus !== "unmounted" && (
              <div
                className="MarqueeContainer"
                style={marqueeContainerAnimationStyles[transitionStatus]}
              >
                {Array.from(new Array(9).keys()).map((_, index) => (
                  <p
                    key={index}
                    style={{
                      animationDelay: `${index * -13}s`,
                      animationDirection:
                        index % 2 === 0 ? "alternate" : "alternate-reverse",
                    }}
                  >
                    {Array.from(new Array(5).keys()).map((_, spanIndex) => (
                      <span key={spanIndex}>
                        {revealVerb} anywhere to reveal
                      </span>
                    ))}
                  </p>
                ))}
              </div>
            )}
            {showsNeedsRetryNotice && cardState && cardState.needsRetry && (
              <div className="NeedsRetryNotice">
                <Retry31 />
                <span>Retrying from an earlier question set</span>
              </div>
            )}
          </div>
        )}
      </Transition>
    </div>
  );
});

export default React.forwardRef(
  (props: CardProps, ref: React.Ref<CardHandle>) => (
    <CardImpl {...props} exportedRef={ref} />
  ),
);

const answerCoverAnimationStyles: { [key: string]: React.CSSProperties } = {
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

const marqueeContainerAnimationStyles: {
  [key: string]: React.CSSProperties;
} = {
  entering: { animationName: "none" },
  entered: { animationName: "none" },
};

function renderTextToHTML(text: string): string {
  return markdownRenderer
    .render(
      text
        .replace(
          /(\s?)([^\s]+?)?___([^\s]+?)?(\s|$)/g,
          `$1<span class="ClozeContainer">$2<span class="Cloze">&nbsp;</span>$3</span>$4`,
        )
        .replace(
          /(\s|^)\$([^$v]+?)\$(\S+?)(\s|$)/g,
          `$1<span class="InlineMathContainer">$$$2$$$3</span>$4`,
        ),
    )
    .replace(
      /<img src="(.+?)\.png"/g,
      `<img src="$1-1x.png" srcset="$1-1x.png 1x, $1.png 2x"`,
    );
}

const RenderedTextRegion = React.memo(function RenderedTextRegion(props: {
  children: string;
  className?: string;
}) {
  return (
    <div
      className={props.className}
      dangerouslySetInnerHTML={{ __html: renderTextToHTML(props.children) }}
    />
  );
});
